import { useState, useEffect, useRef } from 'react';
import Table from '../components/common/Table.jsx';
import PriceCalculatorApiService from '../services/PriceCalculatorApiService';
import DebugService from '../services/DebugService';
import { CONST_SITE_LABEL, setScopedItem, STORAGE_KEYS } from '../components/common/Helper';

const TABS = ['EC2', 'RDS', 'S3', 'EBS'];

const EC2_COLUMNS = [
  { key: 'id', label: 'ID', render: r => <span className="mono-id">{r.id}</span> },
  { key: 'instanceType', label: 'Instance Type', render: r => <strong style={{ color: '#1d4ed8' }}>{r.instanceType}</strong> },
  { key: 'vcpu', label: 'vCPUs', className: 'num' },
  { key: 'memory', label: 'Memory', className: 'num' },
  { key: 'networkPerformance', label: 'Network Performance' },
  { key: 'storage', label: 'Storage' },
  { key: 'currentGeneration', label: 'Current Gen.' },
  { key: 'instanceHourlyCost', label: 'On Demand Hourly', className: 'num', render: r => r.instanceHourlyCost != null ? `$${r.instanceHourlyCost}` : '—' },
  { key: 'instanceMonthlyCost', label: 'On Demand Monthly', className: 'num', render: r => r.instanceMonthlyCost != null ? `$${r.instanceMonthlyCost}` : '—' },
];

const RDS_COLUMNS = [
  { key: 'id', label: 'ID', render: r => <span className="mono-id">{r.id}</span> },
  { key: 'instanceType', label: 'Instance Type', render: r => <strong style={{ color: '#1d4ed8' }}>{r.instanceType}</strong> },
  { key: 'vcpu', label: 'vCPUs', className: 'num' },
  { key: 'memory', label: 'Memory', className: 'num' },
  { key: 'deploymentOption', label: 'Deployment Option' },
  { key: 'storage', label: 'Storage' },
  { key: 'currentGeneration', label: 'Current Gen.' },
  { key: 'instanceHourlyCost', label: 'On Demand Hourly', className: 'num', render: r => r.instanceHourlyCost != null ? `$${r.instanceHourlyCost}` : '—' },
  { key: 'instanceMonthlyCost', label: 'On Demand Monthly', className: 'num', render: r => r.instanceMonthlyCost != null ? `$${r.instanceMonthlyCost}` : '—' },
];

const DEFAULT_FORMS = {
  EC2: {
    region: 'Asia Pacific (Mumbai)', tenancy: 'Shared', operatingSystem: 'Linux',
    selInstanceFamily: '', instanceName: '', selVCPU: '', selMemory: '', selNetworkPerformace: '',
  },
  RDS: {
    region: 'Asia Pacific (Mumbai)', databaseEngine: 'MySQL', deploymentOption: 'Multi-AZ',
    rdsInstanceType: '', selInstanceFamily: '', selVCPU: '', selMemory: '', clusterConfiguration: '',
  },
  S3: { region: 'Asia Pacific (Mumbai)', storageClass: '', storageAmount: 0, storageUnit: '' },
  EBS: {
    region: 'Asia Pacific (Mumbai)', storageType: 'General Purpose SSD (gp2)',
    volumes: 1, volumeDuration: 730, volumeDurationUnit: 'hours per month',
    storageAmount: 30, storageUnit: 'GB',
  },
};

function SelectField({ label, value, onChange, options, placeholder, required }) {
  return (
    <div className="estimator-field">
      <label className="estimator-label">{label}{required && <span style={{ color: '#ef4444' }}> *</span>}</label>
      <select className="estimator-select" value={value} onChange={e => onChange(e.target.value)}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div className="estimator-field">
      <label className="estimator-label">{label}</label>
      <input
        type="number"
        className="estimator-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ cursor: 'text' }}
      />
    </div>
  );
}

export default function CloudEstimator() {
  const [service, setService] = useState('EC2');
  const [form, setForm] = useState(DEFAULT_FORMS.EC2);
  const [priceList, setPriceList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const skipFetchRef = useRef(false);

  const [filters, setFilters] = useState({
    regionList: [], tenancyList: [], operatingSystemList: [], instanceFamilyList: [],
    vcpuList: [], memoryList: [], networkPerformanceList: [], databaseEngineList: [],
    deploymentOptionList: [], clusterConfigurationList: [], instanceTypeList: [],
    storageClassList: [], storageUnitList: [], storageTypeList: [],
  });

  const [ebsResult, setEbsResult] = useState(null);
  const [s3Result, setS3Result] = useState(null);

  const set = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

  const fetchFilters = (svc) => {
    PriceCalculatorApiService.getFilters(svc)
      .then(res => {
        const d = res?.data ?? {};
        setFilters({
          regionList: d.location ?? [],
          tenancyList: d.tenancy ?? [],
          operatingSystemList: d.operatingSystem ?? [],
          instanceFamilyList: d.instanceFamily ?? [],
          vcpuList: d.vcpu ?? [],
          memoryList: d.memory ?? [],
          networkPerformanceList: d.networkPerformance ?? [],
          databaseEngineList: d.databaseEngine ?? [],
          deploymentOptionList: d.deploymentOption ?? [],
          clusterConfigurationList: d.storage ?? [],
          instanceTypeList: d.instanceType ?? [],
          storageClassList: d.storageClass ?? [],
          storageUnitList: d.pricingUnits ?? d.storageAmountUnit ?? [],
          storageTypeList: d.storageType ?? [],
        });
      })
      .catch(err => DebugService.error('CloudEstimator: filter fetch error', err));
  };

  const fetchPriceList = (svc, f) => {
    setIsLoading(true);
    const req = { location: f.region };

    if (svc === 'EC2') {
      req.operatingSystem = f.operatingSystem;
      req.tenancy = f.tenancy;
      req.vcpu = f.selVCPU;
      req.instanceType = f.instanceName;
      req.instanceFamily = f.selInstanceFamily;
      req.memory = f.selMemory;
      req.networkPerformance = f.selNetworkPerformace;
    } else if (svc === 'RDS') {
      req.databaseEngine = f.databaseEngine;
      req.deploymentOption = f.deploymentOption;
      req.instanceType = f.rdsInstanceType;
      req.vcpu = f.selVCPU;
      req.instanceFamily = f.selInstanceFamily;
      req.memory = f.selMemory;
      if (['Aurora PostgreSQL', 'Aurora MySQL'].includes(f.databaseEngine)) req.storage = f.clusterConfiguration;
    } else if (svc === 'S3') {
      req.storageClass = f.storageClass;
      req.pricingUnit = f.storageUnit;
      req.storageAmount = f.storageAmount;
    } else if (svc === 'EBS') {
      req.storageType = f.storageType;
      req.volumeDuration = f.volumeDuration;
      req.volumeDurationUnit = f.volumeDurationUnit;
      req.storageAmount = f.storageAmount;
      req.storageAmountUnit = f.storageUnit;
      req.volumes = f.volumes;
    }

    PriceCalculatorApiService.getPriceList(svc, req)
      .then(res => {
        const d = res?.data ?? [];
        if (svc === 'EBS') {
          setEbsResult(d[0] ?? null);
          setPriceList([]);
        } else if (svc === 'S3') {
          setS3Result(d[0] ?? null);
          setPriceList([]);
        } else {
          setPriceList(d);
        }
        setIsLoading(false);
      })
      .catch(err => {
        DebugService.error('CloudEstimator: price list error', err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Cloud Estimator`;
  }, []);

  useEffect(() => {
    const defaultForm = DEFAULT_FORMS[service];
    skipFetchRef.current = true;
    setPriceList([]);
    setEbsResult(null);
    setS3Result(null);
    setForm(defaultForm);
    fetchFilters(service);
    fetchPriceList(service, defaultForm);
  }, [service]);

  useEffect(() => {
    if (skipFetchRef.current) { skipFetchRef.current = false; return; }
    fetchPriceList(service, form);
  }, [form]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Cloud Estimator',
      service,
      selectedConfiguration: form,
      priceList,
      ebsEstimate: ebsResult,
      s3Estimate: s3Result,
    });
  }, [service, form, priceList, ebsResult, s3Result]);

  const handleReset = () => {
    const defaultForm = DEFAULT_FORMS[service];
    skipFetchRef.current = true;
    setForm(defaultForm);
    setPriceList([]);
    setEbsResult(null);
    setS3Result(null);
    fetchPriceList(service, defaultForm);
  };

  const isTableService = service === 'EC2' || service === 'RDS';
  const columns = service === 'RDS' ? RDS_COLUMNS : EC2_COLUMNS;

  return (
    <>
      <div className="breadcrumb">
        Inventory <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Cloud Estimator</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>Cloud Estimator</h1>
          <div className="meta">Compare EC2, RDS, S3 and EBS pricing across regions and configurations</div>
        </div>
      </div>

      <div className="estimator-stage">
        {/* Tabs */}
        <div className="tab-row" style={{ marginBottom: 18 }}>
          {TABS.map(t => (
            <button key={t} className={`tab${service === t ? ' active' : ''}`} onClick={() => setService(t)}>
              {t}
            </button>
          ))}
        </div>

        {/* Reset */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button className="btn primary" onClick={handleReset}>Reset</button>
        </div>

        {/* Filters */}
        <div className="estimator-filter-grid">
          {service === 'EC2' && <>
            <SelectField label="Region" value={form.region} onChange={set('region')} options={filters.regionList} required />
            <SelectField label="Tenancy" value={form.tenancy} onChange={set('tenancy')} options={filters.tenancyList} required />
            <SelectField label="Operating System" value={form.operatingSystem} onChange={set('operatingSystem')} options={filters.operatingSystemList} required />
            <SelectField label="Instance Family" value={form.selInstanceFamily} onChange={set('selInstanceFamily')} options={filters.instanceFamilyList} placeholder="Select…" />
            <SelectField label="Instance Type" value={form.instanceName} onChange={set('instanceName')} options={filters.instanceTypeList} placeholder="Select…" />
            <SelectField label="vCPUs" value={form.selVCPU} onChange={set('selVCPU')} options={filters.vcpuList} placeholder="Select…" />
            <SelectField label="Memory" value={form.selMemory} onChange={set('selMemory')} options={filters.memoryList} placeholder="Select…" />
            <SelectField label="Network Performance" value={form.selNetworkPerformace} onChange={set('selNetworkPerformace')} options={filters.networkPerformanceList} placeholder="Select…" />
          </>}

          {service === 'RDS' && <>
            <SelectField label="Region" value={form.region} onChange={set('region')} options={filters.regionList} required />
            <SelectField label="Database Engine" value={form.databaseEngine} onChange={set('databaseEngine')} options={filters.databaseEngineList} required />
            <SelectField label="Deployment Option" value={form.deploymentOption} onChange={set('deploymentOption')} options={filters.deploymentOptionList} required />
            <SelectField label="Instance Type" value={form.rdsInstanceType} onChange={set('rdsInstanceType')} options={filters.instanceTypeList} placeholder="Select…" />
            <SelectField label="Instance Family" value={form.selInstanceFamily} onChange={set('selInstanceFamily')} options={filters.instanceFamilyList} placeholder="Select…" />
            <SelectField label="vCPUs" value={form.selVCPU} onChange={set('selVCPU')} options={filters.vcpuList} placeholder="Select…" />
            <SelectField label="Memory" value={form.selMemory} onChange={set('selMemory')} options={filters.memoryList} placeholder="Select…" />
            {['Aurora PostgreSQL', 'Aurora MySQL'].includes(form.databaseEngine) && (
              <SelectField label="Cluster Configuration" value={form.clusterConfiguration} onChange={set('clusterConfiguration')} options={filters.clusterConfigurationList} placeholder="Select…" />
            )}
          </>}

          {service === 'S3' && <>
            <SelectField label="Region" value={form.region} onChange={set('region')} options={filters.regionList} required />
            <SelectField label="Storage Class" value={form.storageClass} onChange={set('storageClass')} options={filters.storageClassList} placeholder="Select…" required />
            <SelectField label="Pricing Unit" value={form.storageUnit} onChange={set('storageUnit')} options={filters.storageUnitList} placeholder="Select…" required />
            <NumberField label="Storage Amount" value={form.storageAmount} onChange={set('storageAmount')} />
          </>}

          {service === 'EBS' && <>
            <SelectField label="Region" value={form.region} onChange={set('region')} options={filters.regionList} required />
            <SelectField label="Storage Type" value={form.storageType} onChange={set('storageType')} options={filters.storageTypeList} required />
            <SelectField label="Storage Unit" value={form.storageUnit} onChange={set('storageUnit')} options={filters.storageUnitList} placeholder="Select…" />
            <NumberField label="Storage Amount" value={form.storageAmount} onChange={set('storageAmount')} />
            <NumberField label="Volumes" value={form.volumes} onChange={set('volumes')} />
            <NumberField label="Volume Duration (hrs/mo)" value={form.volumeDuration} onChange={set('volumeDuration')} />
          </>}
        </div>

        {/* S3 result */}
        {service === 'S3' && s3Result && (
          <div className="estimator-result-cards">
            <div className="estimator-result-card">
              <div className="lbl">On-demand Cost</div>
              <div className="val">${s3Result.storageOndemandCost ?? '—'}</div>
            </div>
            <div className="estimator-result-card">
              <div className="lbl">Monthly Cost</div>
              <div className="val">${s3Result.storageOndemandMonthlyCost ?? '—'}</div>
            </div>
          </div>
        )}

        {/* EBS result */}
        {service === 'EBS' && ebsResult && (
          <div className="estimator-result-cards">
            <div className="estimator-result-card">
              <div className="lbl">Storage</div>
              <div className="val">{ebsResult.storageInGB ?? '—'} GB</div>
            </div>
            <div className="estimator-result-card">
              <div className="lbl">On-demand Cost</div>
              <div className="val">${ebsResult.ebsOndemandCost ?? '—'}/hr</div>
            </div>
            <div className="estimator-result-card">
              <div className="lbl">Monthly Storage Cost</div>
              <div className="val">{ebsResult.ebsStorageCost ?? '—'}</div>
            </div>
            <div className="estimator-result-card">
              <div className="lbl">Total Hours</div>
              <div className="val">{((form.volumes ?? 0) * (form.volumeDuration ?? 0)).toFixed(0)} hrs</div>
            </div>
          </div>
        )}

        {/* EC2 / RDS table */}
        {isTableService && (
          <Table
            rows={priceList}
            columns={columns}
            loading={isLoading}
            defaultPageSize={100}
            exportFilename={`cloud_estimator_${service.toLowerCase()}`}
            subscriptionGated
          />
        )}
      </div>
    </>
  );
}
